import { useState, useCallback, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { selectAccountId } from '../../../store/slices/authorizationSlice';

/**
 * Stock-owner switcher — **UI hook (presentation)**.
 *
 * The previous implementation depended on RTK APIs that lived under removed
 * `domains/stockOwner` and `domains/auth` folders. Until those bounded contexts
 * are ported back, this hook returns an **empty** list so the header still
 * compiles and the dropdown stays hidden (`StockOwnerManagement` returns null).
 */

export interface DropdownPosition {
  top: number;
  left: number;
}

/** Minimal row shape for future stock-owner bounded context */
export interface StockOwnerRow {
  id: number;
  name: string;
  isActive?: boolean;
  brandImageUrl?: string | null;
}

export const useStockOwnerManagement = () => {
  const triggerRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<DropdownPosition>({
    top: 0,
    left: 0,
  });

  const stockOwners: StockOwnerRow[] = [];
  const isLoading = false;
  const isSwitching = false;
  const isError = false;

  const activeStockOwnerId = useSelector(selectAccountId) as number | null;
  const activeStockOwner = undefined as StockOwnerRow | undefined;

  const handleToggle = useCallback(() => {
    if (!isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 8,
        left: rect.left,
      });
    }
    setIsOpen(prev => !prev);
  }, [isOpen]);

  const handleClose = useCallback(() => setIsOpen(false), []);

  const handleSelectStockOwner = useCallback(
    async (_stockOwner: StockOwnerRow) => {
      handleClose();
    },
    [handleClose],
  );

  useEffect(() => {
    if (!isOpen) return;
    const onOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (
        triggerRef.current?.contains(target) ||
        target.closest('.stock-owner-management__dropdown')
      ) {
        return;
      }
      handleClose();
    };
    document.addEventListener('mousedown', onOutside);
    return () => document.removeEventListener('mousedown', onOutside);
  }, [isOpen, handleClose]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && handleClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, handleClose]);

  return {
    isOpen,
    isLoading,
    isSwitching,
    isError,
    stockOwners,
    activeStockOwner,
    activeStockOwnerId,
    triggerRef,
    dropdownPosition,
    handleToggle,
    handleClose,
    handleSelectStockOwner,
  };
};
